import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useLangContext } from "context/Lang";
import { AsyncProcess } from "services/AsyncProcess";
import { routes } from "routes";
import { Button } from "components/Button/Button";
import { Loader } from "components/Loader/Loader";
import { motion } from "framer-motion";
import { UserPlus, ExternalLink, ShieldCheck, ArrowLeft, Building2, BoxesIcon, SaveIcon, XIcon, Trash2Icon } from "lucide-react";
import { DialogModal } from "components/dialog-modal/dialog-modal";
import { PreviewBuilding } from "./preview-building";
import { useDispatch } from "react-redux";
import { setToast } from "storeRedux/slices/toast";
import { Message } from "primereact/message";
import { AddEditorDialogContent } from "./add-editor-dialog-content";

export const Building = () => {
    

    const navigate = useNavigate();
    const { gW } = useLangContext();
    const { id } = useParams() as { id: any };
    const [openEditorDialog,setOpenEditorDialog] = useState<boolean>(false);
    const [editorsIds,setEditorsIds] = useState<number[]>([]);
    const [deleteEditorDialog,setDeleteEditorDialog] = useState<EditorItemType|null>(null);

    const [editors,setEditors] = useState<EditorItemType[]>([]);

    const dispatch = useDispatch();

    const [permission, setPermission] = useState<boolean>(true);

    const [isPreviewOpen, setIsPreviewOpen] = useState<EditorItemType|null>(null);


    const [loading, setLoading] = useState(true);
    const [building, setBuilding] = useState<Building | null>(null);



    const openDeleteConfirm = (editor: EditorItemType) => {
        setDeleteEditorDialog(editor);
    };

    const confirmRemoveEditor = () => {
        if (!deleteEditorDialog?.id) return;
        
        setLoading(true);
        const editorId = deleteEditorDialog.id;

        AsyncProcess({
            type: "post",
            url: `${routes.api.threeItemEditor}/delete/${editorId}`,
            setError: () => {
                dispatch(setToast({ detail: gW("happen-error"), severity: "error" }));
                setLoading(false);
                setDeleteEditorDialog(null);
            },
            effectProcess: [(res) => {
                if (res?.success !== false) {
                    setEditors(prev => prev.filter(e => e.id !== editorId));
                    dispatch(setToast({ detail: gW("editor-removed"), severity: "success" }));
                } else {
                    dispatch(setToast({ detail: gW(res?.error || "happen-error"), severity: "error" }));
                }
                setLoading(false);
                setDeleteEditorDialog(null);
            }]
        });
    };

    const fetchBuildingDetails = async () => {
        setLoading(true);
        AsyncProcess({
            type: "get",
            url: `${routes.api.buildings}/${id}`,
            setError: () => { setLoading(false) },
            effectProcess: [(res) => {
                if (res?.success === false) {
                    dispatch(
                        setToast({
                            detail: gW(res?.message),
                            severity: "error",
                        })
                    )
                    setPermission(false);
                    setLoading(false);
                
                } else {
                    setBuilding(res?.data)
                    getEditors(res?.data?.id)
                }
            }],
        });

    };

    useEffect(() => {
        if (id) fetchBuildingDetails();
    }, [id]);

    const getEditors=(id?:number)=>{
        setLoading(true);

        AsyncProcess({
            type:"get",
            url:routes.api.threeItemEditor,
            data:{
                params:{
                    buildingId:id
                }
            },
            setError:()=>{
                dispatch(
                    setToast({
                        detail: gW("happen-error"),
                        severity: "error",
                    })
                )
                setLoading(false);
            },
            effectProcess:[(res)=>{
                 if(res?.success===false){
                    dispatch(
                        setToast({
                            detail: gW(res?.message),
                            severity: "error",
                        })
                    )

                }else {
                    setEditors(res?.data);
                }       
                setLoading(false);   
            }]

        })
    }


    const handleSaveEditors=()=>{

        setLoading(true);

        AsyncProcess({
            type:"post",
            url:routes.api.threeItemEditor,
            data:{
                "editorsIds":editorsIds,
                "buildingId":building?.id
            },
            setError:()=>{

                setLoading(false);
                dispatch(
                    setToast({
                        detail: gW("happen-error"),
                        severity: "error",
                    })
                )
            
            },
            effectProcess:[(res)=>{
                if(res?.success===false){
                    dispatch(
                        setToast({
                            detail: gW(res?.message),
                            severity: "error",
                        })
                    )

                }else {
                    setEditors(res?.data);
                    setOpenEditorDialog(false)
                }       
                setLoading(false);      
            }]
        })


    }

    const handleSetActive = (e:EditorItemType) => {
        

        setLoading(true);
        AsyncProcess({
            type: "post", // أو "put" حسب تصميم الـ API لديك
            url: `${routes.api.threeItemEditor}/set-active/${e.id}`,
            setError: () => {
                setLoading(false);
                dispatch(setToast({ detail: gW("happen-error"), severity: "error" }));
            },
            effectProcess: [(res) => {
                if (res?.success !== false) {
                    dispatch(setToast({ detail: gW("set-active-success"), severity: "success" }));
                    setIsPreviewOpen(null);
                    setBuilding(prev=>({...prev||{},activeEditor:res?.data?.editorItem as EditorItemType  }))
                    // fetchBuildingDetails(); 
                } else {
                    dispatch(setToast({ detail: gW(res?.message || "happen-error"), severity: "error" }));
                }
                setLoading(false);
            }]
        });
    };


    const editorCard=({editor,isActive}:{editor:EditorItemType,isActive?:boolean})=>{
        return <div key={editor.id} className="flex items-center justify-between p-3 bg-primary-50/20 rounded-xl border border-primary-100">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold">
                    {editor.owner?.name?.[0] +""+editor.owner?.surname?.[0] }
                </div>
                <div>
                    <p className="text-sm font-medium text-primary-800">{editor.owner?.name} {editor.owner?.surname}</p>
                    <p className="text-xs text-primary-500">{editor.owner?.username}</p>
                </div>
            </div>
            
            <div className="flex items-center gap-2">

                <Button 
                    
                    variant= {isActive ?"solid":"outline"} 
                    onClick={()=>!isActive&&handleSetActive(editor)} // ربط الدالة هنا
                    className= {isActive?"":"border-primary-600 text-primary-600 hover:bg-primary-50"} 
                >
                    <ShieldCheck size={18} className="mr-2" />
                    {gW(isActive?"active":"set-as-active")}
                </Button>

                <Button 
                    variant="ghost" 
                    className="text-primary-500 hover:bg-primary-50 p-2 h-auto"
                    onClick={() => setIsPreviewOpen(editor)}
                >
                    <BoxesIcon size={18} />
                    {gW("view-building")}
                </Button>

                <Button 
                    variant="outline" 
                    className=" border-red-500 text-red-500 hover:bg-red-50 p-2 h-auto"
                    onClick={() => openDeleteConfirm(editor)}
                >
                    <Trash2Icon size={16} />
                </Button>
            </div>
        </div>
    }
   

    if (loading) return <div className="flex justify-center p-20"><Loader /></div>;

    if (!permission) return <Message className=" w-full " severity="warn" text={gW("no-permission")} />


    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className=" mx-auto"
        >
            {/* Header / Navigation */}
            <div className="flex items-center gap-4 mb-8">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-primary-800">{building?.title}</h1>
                    <p className="text-primary-500 flex items-center gap-1">
                        <Building2 size={16} /> {building?.unitName}
                    </p>
                </div>
            </div>

         

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                {/* Main Info Card */}
                <div className="md:col-span-2 bg-white rounded-2xl border border-primary-100 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold mb-4 border-b pb-2">{gW("building-details")}</h3>
                    <div className="space-y-4">
                        <div className="flex justify-between py-2 border-b border-primary-50">
                            <span className="text-primary-500">{gW("building-id")}</span>
                            <span className="font-medium">#{building?.id}</span>
                        </div>
                    </div>
                </div>

                {/* Actions Sidebar */}
                <div className="flex flex-col gap-4">
                    <div className="bg-primary-50/50 p-6 rounded-2xl border border-primary-100">
                        <h3 className="text-primary-800 font-bold mb-4 flex items-center gap-2">
                            <ShieldCheck size={18} /> {gW("actions")}
                        </h3>

                        <div className="flex flex-col gap-3">
                            {/* <Button

                                className="w-full justify-start gap-2 bg-primary-600 hover:bg-primary-700 text-white"
                                onClick={() => navigate(routes.editor + `?buildingId=${id}`)}
                            >
                                <ExternalLink size={18} />
                                {gW("open-editor")}
                            </Button> */}

                            <Button
                                variant="outline"
                                className="w-full justify-start gap-2 border-primary-200 text-primary-700 hover:bg-primary-50"
                                onClick={() => setOpenEditorDialog(true)}
                            >
                                <UserPlus size={18} />
                                {gW("add-editor")}
                            </Button>

                            {/* إدارة الصلاحيات */}
                            <Button
                                disabled={!building?.activeEditor?.id}
                                variant="ghost"
                                className="w-full justify-start gap-2 text-primary-600 hover:text-primary-600"
                                onClick={() => setIsPreviewOpen(building?.activeEditor!)}
                            >
                                <BoxesIcon size={18} />
                                {gW(!building?.activeEditor?.id?"no-active-building":"view-active-building")}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="md:col-span-2 mt-6 bg-white rounded-2xl border border-primary-100 p-6 shadow-sm">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <ShieldCheck size={18} className="text-primary-600" />
                    {gW("manage-editors")}
                </h3>
                
                <div className="space-y-3">
                    {editors.length === 0 ? (
                        <p className="text-primary-400 italic text-sm">{gW("no-editors-assigned")}</p>
                    ) : (<>
                        {
                            building?.activeEditor?.id?<>
                                {editorCard({editor: building?.activeEditor,isActive:true})}
                            </>:<>{gW("no-active-building")}</>
                        }
                        {
                            editors?.filter(e=>e?.id!==building?.activeEditor?.id).map((editor) => (
                                editorCard({editor})
                            ))
                        }
                        
                    </>
                        
                    )}
                </div>
            </div>
            <DialogModal
                size="md"
                isOpen={!!deleteEditorDialog}
                onClose={() => setDeleteEditorDialog(null)}
                title={gW("confirm-delete")}
                actions={[
                    <Button 
                        variant="ghost" 
                        onClick={() => setDeleteEditorDialog(null)}
                    >
                        {gW("cancel")}
                    </Button>,
                    <Button 
                        className="bg-red-600 hover:bg-red-700 text-white" 
                        onClick={confirmRemoveEditor}
                    >
                        <Trash2Icon className="mr-2" size={16} />
                        {gW("delete")}
                    </Button>
                ]}
            >
                <div className="p-4 text-center">
                    <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Trash2Icon className="text-red-600" size={32} />
                    </div>
                    <p className="text-primary-600">
                        {gW("are-you-sure-delete-editor")}
                    </p>
                    <p className="font-bold text-primary-800 mt-2">
                        {deleteEditorDialog?.owner?.name} {deleteEditorDialog?.owner?.surname}
                    </p>
                </div>
            </DialogModal>
            <DialogModal
                size="2xl"
                isOpen={isPreviewOpen!==null}
                onClose={() => setIsPreviewOpen(null)}
                title={`${gW("preview")}: ${building?.title}`}
                actions={[<Button 
                    variant="outline" 
                    onClick={()=>handleSetActive(isPreviewOpen!)} // ربط الدالة هنا
                    className="border-primary-600 text-primary-600 hover:bg-primary-50"
                >
                    <ShieldCheck size={18} className="mr-2" />
                    {gW("set-as-active")}
                </Button>]}
            >
                {isPreviewOpen && <PreviewBuilding data={isPreviewOpen} />}
            </DialogModal>

            <DialogModal
                size="3xl"
                isOpen={openEditorDialog}
                onClose={() => setOpenEditorDialog(false)}
                title={`${gW("add-editor")}`}
                actions={[
                    <Button variant="outline"   onClick={handleSaveEditors}>
                        <SaveIcon/>
                        {gW("save")}
                    </Button>
                ]}
            >
                {openEditorDialog && <AddEditorDialogContent
                    initEditorIds={editors?.map(e=>e?.owner?.id) as number[]}
                    onChangeEditorsIds={(e)=>{
                        setEditorsIds(e)
                    }}

                />}
            </DialogModal>
        </motion.div>
    );
};